// Allow observing the scroll progress in an element
//
// Example: x-scroll-progress="console.log($scrollOffset, $height)"

/**
 * @param {string} value
 * @returns {{ value: number, unit: 'px' | 'vh' | undefined }}
 */
function parseMargin(value) {
  const match = value.match(/^(?<value>-?\d+(?:\.\d+)?)(?<unit>px|vh)?$/)
  if (!match) return
  return {
    value: Number(match.groups.value),
    unit: match.groups.unit || undefined,
  }
}

/**
 * @param {import('alpinejs')} Alpine
 */
export default Alpine => {
  Alpine.directive(
    'scroll-progress',
    (el, { expression, modifiers: rawModifiers, value }, { evaluateLater, cleanup }) => {
      /**
       * @type {Map<string, boolean>}
       */
      const flags = new Map()
      /**
       * @type {{ value: number, unit: 'px' | 'vh' | undefined } | undefined}
       */
      let marginTop, marginBottom
      for (let i = 0; i < rawModifiers.length; i++) {
        const key = rawModifiers[i]

        if (key === 'margin-top' || key === 'margin-bottom') {
          const value = parseMargin(rawModifiers[i + 1] ?? '0')

          if (value) {
            if (key === 'margin-top') marginTop = value
            else marginBottom = value
          }
          i++
        } else {
          flags.set(key, true)
        }
      }

      const marginTopFraction = marginTop ? marginTop.value / 100 : 0
      const marginBottomFraction = marginBottom ? marginBottom.value / 100 : 0
      const clamp = !flags.has('no-clamp')
      const expensivePositionCalculation = flags.has('expensive-position-calculation')

      // --------------------------------- //

      //
      // DOM Interactions
      //

      // --------------------------------- //

      //
      // Viewport size
      // The size of the viewport
      //

      // Declare state
      const viewportSizeState = Alpine.reactive({
        value: getViewportSize(),
      })

      // Get value
      function getViewportSize() {
        return {
          width: window.visualViewport.width,
          height: window.visualViewport.height,
        }
      }

      // Update value
      function updateViewportSize() {
        viewportSizeState.value = getViewportSize()
      }

      function viewportResizeListener() {
        updateViewportSize()
      }

      // Watch viewport size
      function attachViewportSizeListener() {
        detachViewportSizeListener()
        window.visualViewport.addEventListener('resize', viewportResizeListener)
      }

      // Detach listener
      function detachViewportSizeListener() {
        window.visualViewport.removeEventListener('resize', viewportResizeListener)
      }

      // --------------------------------- //

      //
      // Element size
      // The size of the element
      //

      const rect = el.getBoundingClientRect()

      // Declare state
      const elementSizeState = Alpine.reactive({
        value: getElementSize(),
      })

      // Get value
      function getElementSize({ width, height } = el.getBoundingClientRect()) {
        return { width, height }
      }

      // Update value
      function updateElementSize(size) {
        const newSize = getElementSize(size)
        if (
          newSize.width !== elementSizeState.value.width ||
          newSize.height !== elementSizeState.value.height
        ) {
          elementSizeState.value = newSize
        }
      }

      // Attach listener
      let resizeObserver
      function attachElementSizeListener() {
        detachElementSizeListener()

        resizeObserver = new ResizeObserver(entries => {
          for (const entry of entries) {
            updateElementSize({
              width: entry.borderBoxSize[0].inlineSize,
              height: entry.borderBoxSize[0].blockSize,
            })
          }
        })
        resizeObserver.observe(el)
      }

      // Detach listener
      function detachElementSizeListener() {
        resizeObserver?.disconnect()
      }

      // --------------------------------- //

      //
      // Scroll position
      // The distance from the top of the document to the top of the viewport
      //

      // Declare state
      const scrollPosition = Alpine.reactive({
        value: getScrollY(),
      })

      // Get value
      function getScrollY() {
        return Math.max(window.scrollY, 0)
      }

      // Update value
      function updateScrollY() {
        scrollPosition.value = getScrollY()
      }

      function scrollListener() {
        updateScrollY()

        if (expensivePositionCalculation) {
          updateScrollOffsetState()
        }
      }

      // Attach listener
      function attachScrollListener() {
        detachScrollListener()
        // Watch scroll position
        window.addEventListener('scroll', scrollListener, { passive: true })
      }

      // Detach listener
      function detachScrollListener() {
        window.removeEventListener('scroll', scrollListener)
      }

      // --------------------------------- //

      //
      // Scroll offset
      // The distance from the top of the element to the top of the document
      //

      // Declare state
      const scrollOffsetState = Alpine.reactive({
        value: getScrollOffsetState(),
      })

      // Get value
      function getScrollOffsetState() {
        return el.getBoundingClientRect().top + scrollPosition.value
      }

      // Update value
      const updateScrollOffsetState = Alpine.debounce(function updateScrollOffsetState() {
        scrollOffsetState.value = el.getBoundingClientRect().top + scrollPosition.value
      }, 0)

      function scrollOffsetListener() {
        updateScrollOffsetState()
      }

      // Attach listener
      function attachScrollOffsetListener() {
        if (!expensivePositionCalculation) return

        detachScrollOffsetListener()

        // Watch scroll position
        window.addEventListener('scroll', scrollOffsetListener, { passive: true })
      }

      // Detach listener
      function detachScrollOffsetListener() {
        window.removeEventListener('scroll', scrollOffsetListener)
      }

      // --------------------------------- //

      // Attach listeners
      function attachAllListeners() {
        attachViewportSizeListener()
        attachElementSizeListener()
        attachScrollListener()
        attachScrollOffsetListener()
      }

      // Detach listeners
      function detachAllListeners() {
        detachViewportSizeListener()
        detachElementSizeListener()
        detachScrollListener()
        detachScrollOffsetListener()
      }

      // --------------------------------- //

      //
      // State
      //

      // --------------------------------- //

      // Recalculate the scroll offset when the element or viewport size changes
      Alpine.effect(() => {
        // Reference all reactive properties to trigger the effect
        ;(() => [
          viewportSizeState.value,
          elementSizeState.value.width,
          elementSizeState.value.height,
        ])()

        updateScrollOffsetState()
      })

      attachAllListeners()

      const outputState = Alpine.reactive({
        get progress() {
          const marginTopReference = !marginTop?.unit
            ? Math.min(elementSizeState.value.height, viewportSizeState.value.height)
            : marginTop.unit === 'px'
              ? 100
              : viewportSizeState.value.height
          const marginTopOffset = marginTopFraction * marginTopReference

          const marginBottomReference = !marginBottom?.unit
            ? Math.min(elementSizeState.value.height, viewportSizeState.value.height)
            : marginBottom.unit === 'px'
              ? 100
              : viewportSizeState.value.height
          const marginBottomOffset = marginBottomFraction * marginBottomReference
          const maxScroll = elementSizeState.value.height + marginTopOffset + marginBottomOffset
          const currentScroll =
            viewportSizeState.value.height +
            scrollPosition.value -
            scrollOffsetState.value +
            marginTopOffset
          const scrollProgress = currentScroll / maxScroll
          return clamp
            ? Math.round(Math.min(Math.max(scrollProgress, 0), 1) * 10 ** 3) / 10 ** 3
            : scrollProgress
        },
      })

      Alpine.addScopeToNode(el, {
        get $scrollProgress() {
          return outputState.progress
        },
      })

      const runEffect = expression.length > 0 ? evaluateLater(expression) : () => {}
      Alpine.effect(() => {
        runEffect()
      })

      cleanup(() => {
        detachAllListeners()
      })
    },
  )
}
